/**
 * Admin-side panel showing customer-initiated requests against a published test.
 *
 * Renders inside `LabDetail.jsx` once a test reaches published status. The lab
 * manager can see whether the customer clicked "Request Instant Proposal" or
 * "Book Free Post Water Test Assessment", and move each request through the
 * lifecycle (New → Contacted → Proposal sent / Assessment booked → Closed).
 *
 * Lifecycle states (mirrored from backend `customer_requests_service.ALLOWED_STATUSES`):
 *   new, contacted, proposal_sent, assessment_booked, closed
 */
import { useEffect, useState } from "react";
import { MailCheck, Send, CalendarCheck2, RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "new",                     label: "New",                    tone: "bg-cyan-100 text-cyan-800",    open: true },
  { value: "contacted",               label: "Contacted",              tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "in_progress",             label: "In progress",            tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "proposal_being_prepared", label: "Estimate being prepared", tone: "bg-amber-100 text-amber-800", open: true },
  { value: "assessment_requested",    label: "Assessment requested",   tone: "bg-amber-100 text-amber-800",  open: true },
  { value: "proposal_sent",           label: "Estimate sent",          tone: "bg-indigo-100 text-indigo-800", open: false },
  { value: "assessment_booked",       label: "Assessment booked",      tone: "bg-violet-100 text-violet-800", open: false },
  { value: "closed",                  label: "Closed",                 tone: "bg-slate-200 text-slate-700",  open: false },
  { value: "cancelled",               label: "Cancelled",              tone: "bg-slate-200 text-slate-700",  open: false },
  { value: "duplicate",               label: "Duplicate",              tone: "bg-slate-200 text-slate-700",  open: false },
];

const TYPE_LABEL = {
  proposal:   { label: "Instant Estimate",            icon: Send },
  assessment: { label: "Free Post Water Test Assessment", icon: CalendarCheck2 },
};

export function CustomerRequestsAdminPanel({ testId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customer-requests?test_id=${encodeURIComponent(testId)}`);
      setRequests(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load customer requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (testId) load(); }, [testId]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/admin/customer-requests/${id}`, { status: newStatus });
      setRequests((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update status");
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <section data-testid="admin-customer-requests-panel" className="rounded-3xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading customer requests…</div>
      </section>
    );
  }

  if (requests.length === 0) {
    return (
      <section data-testid="admin-customer-requests-panel" className="rounded-3xl bg-white border border-dashed border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <MailCheck className="w-4 h-4 text-slate-400" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Customer follow-up requests</h2>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          The customer has not yet requested an Instant Proposal or a Free Post Water Test Assessment for this test. Buttons appear on their published Summary Report when corrective recommendations are present.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="admin-customer-requests-panel" className="rounded-3xl bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MailCheck className="w-4 h-4 text-cyan-600" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Customer follow-up requests</h2>
          <span data-testid="admin-customer-requests-count" className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-100 text-cyan-800">
            {requests.length}
          </span>
        </div>
        <button onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="admin-customer-requests-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-3">
        {requests.map((r) => {
          const meta = TYPE_LABEL[r.type] || { label: r.type, icon: MailCheck };
          const Icon = meta.icon;
          const statusOpt = STATUS_OPTIONS.find((s) => s.value === r.status) || STATUS_OPTIONS[0];
          return (
            <div key={r.id} data-testid={`admin-customer-request-${r.id}`} className="rounded-2xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-cyan-600" />
                  <div>
                    <div className="font-display font-semibold text-slate-900 text-sm">{meta.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {formatDateTimeAU(r.created_at)}
                    </div>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusOpt.tone}`}>{statusOpt.label}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
                <div><strong className="text-slate-900">{r.customer?.name || "—"}</strong> · <a href={`mailto:${r.customer?.email}`} className="text-cyan-700">{r.customer?.email}</a></div>
                <div>{r.customer?.phone || "—"}</div>
                <div className="sm:col-span-2">{r.customer?.address || "—"}</div>
                {r.preferred_datetime && <div><span className="text-slate-400">Preferred:</span> {r.preferred_datetime}</div>}
              </div>
              {(r.flagged_rule_keys?.length > 0 || r.suggested_services?.length > 0) && (
                <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-2.5 text-[11px] space-y-1">
                  {r.flagged_rule_keys?.length > 0 && (
                    <div><span className="text-slate-500 font-semibold uppercase tracking-wider mr-1.5">Rule keys:</span><span className="font-mono text-slate-800">{r.flagged_rule_keys.join(", ")}</span></div>
                  )}
                  {r.suggested_services?.length > 0 && (
                    <div><span className="text-slate-500 font-semibold uppercase tracking-wider mr-1.5">Services:</span><span className="text-slate-800">{r.suggested_services.join(", ")}</span></div>
                  )}
                  {r.adwg_noncompliant?.length > 0 && (
                    <div><span className="text-slate-500 font-semibold uppercase tracking-wider mr-1.5">ADWG:</span><span className="text-slate-800">{r.adwg_noncompliant.join(", ")}</span></div>
                  )}
                </div>
              )}
              {r.notes && (
                <div className="mt-2 text-xs text-slate-700 italic">"{r.notes}"</div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Status</div>
                <select
                  data-testid={`admin-customer-request-status-${r.id}`}
                  value={r.status}
                  onChange={(e) => updateStatus(r.id, e.target.value)}
                  disabled={updatingId === r.id}
                  className="text-xs rounded-lg border border-slate-300 px-2 py-1.5 focus:outline-none focus:border-cyan-500"
                >
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {updatingId === r.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
